import { AppProps } from "next/app";
import Head from "next/head";
import "./global.css";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorReportProvider } from "@/components/ErrorReportProvider";
import { Toaster } from "@/components/ui/toaster";
import { SWRConfig } from "swr";
import { fetcher, isProd } from "@/lib/utils";
import { ThemeProvider } from "next-themes";
import Script from "next/script";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { swrLogger } from "@/components/swrLogger";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { AnalyticsConsent } from "@/components/analytics/AnalyticsConsent";
import { GoogleTagManager } from "@/components/analytics/GoogleTagManager";
import { useSendAnalyticsEvent } from "@/components/analytics/GoogleTagManager";
import { Inter, Instrument_Serif, JetBrains_Mono } from "next/font/google";
import { getSeo } from "@/components/useSeo";
import { SessionProvider } from "@/components/useSession";

const inter = Inter({ subsets: ["latin", "cyrillic"], variable: "--font-inter" });
const instrumentSerif = Instrument_Serif({
  weight: ["400"],
  style: ["normal", "italic"],
  subsets: ["latin"],
  variable: "--font-instrument-serif"
});
const jetbrainsMono = JetBrains_Mono({
  subsets: ["latin", "cyrillic"],
  variable: "--font-jetbrains-mono"
});

export default function App({ Component, pageProps, router }: AppProps) {
  const sendEvent = useSendAnalyticsEvent();
  const seo = getSeo(router.pathname);
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL ?? "https://dollardeploy.com";
  const canonicalPath = (router.asPath ?? "/").split(/[?#]/)[0];
  const canonicalUrl = new URL(canonicalPath, baseUrl).toString();
  const imageUrl = new URL(seo.image, baseUrl).toString();
  const noIndexPaths = [
    "/dashboard",
    "/app",
    "/host",
    "/task",
    "/journal",
    "/backup",
    "/settings",
    "/auth"
  ];
  const shouldNoIndex = noIndexPaths.some(path => router.pathname.startsWith(path));
  return (
    <main
      className={`${inter.className} ${instrumentSerif.variable} ${jetbrainsMono.variable} font-sans`}
    >
      {/* https://nextjs.org/docs/pages/api-reference/components/head */}
      <Head>
        {/* Primary */}
        <title>{`${seo.title} | ${seo.name}`}</title>
        <meta name="description" key="description" content={seo.description} />
        <meta name="keywords" key="keywords" content={seo.keywords} />
        <meta name="author" content="Wizecore Oy" />
        <meta
          name="robots"
          content={
            shouldNoIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
          }
        />
        <meta name="theme-color" content="#f6f3ee" />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1.0"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DollarDeploy" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seo.title} key="title" />
        <meta property="og:description" key="og:description" content={seo.description} />
        <meta property="og:image" content={imageUrl} key="image" />
        <meta property="og:image:secure_url" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`DollarDeploy — ${seo.title}`} />
        <meta property="og:locale" content="en_US" />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@dollardeploy" />
        <meta name="twitter:creator" content="@dollardeploy" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={imageUrl} key="twitter:image" />
        <meta name="twitter:image:alt" content={`DollarDeploy — ${seo.title}`} />

        {/* PWA / Mobile */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="DollarDeploy" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />

        {/* Favicons */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" href="/icon.png" />
        <link rel="apple-touch-icon" href="/icon.png" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: "DollarDeploy",
              url: baseUrl,
              description:
                "Chat-first AI deployment platform. Deploy your Next.js, Python, Go, or Docker app to your own server in minutes — no DevOps required.",
              applicationCategory: "DeveloperApplication",
              operatingSystem: "Web",
              offers: {
                "@type": "Offer",
                price: "8.79",
                priceCurrency: "USD",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: "8.79",
                  priceCurrency: "USD",
                  billingIncrement: 1,
                  unitCode: "MON"
                }
              },
              creator: {
                "@type": "Organization",
                name: "Wizecore Oy",
                url: `${baseUrl}/team`,
                logo: `${baseUrl}/logo-main.svg`,
                sameAs: [
                  "https://x.com/dollardeploy",
                  "https://github.com/dollardeploy",
                  "https://www.linkedin.com/company/dollardeploy",
                  "https://youtube.com/@dollardeploy"
                ]
              },
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "4.9",
                reviewCount: "47"
              }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "DollarDeploy",
              legalName: "Wizecore Oy",
              url: baseUrl,
              logo: `${baseUrl}/logo-main.svg`,
              foundingLocation: "Helsinki, Finland",
              sameAs: [
                "https://x.com/dollardeploy",
                "https://github.com/dollardeploy",
                "https://www.linkedin.com/company/dollardeploy",
                "https://youtube.com/@dollardeploy",
                `${baseUrl}/discord`
              ]
            })
          }}
        />
      </Head>

      {/* https://plausible.io/docs/custom-event-goals */}
      {process.env.NEXT_PUBLIC_APP_URL &&
        isProd() &&
        process.env.NEXT_PUBLIC_PLAUSIBLE === "1" && (
          <Script
            async
            defer
            data-domain={new URL(process.env.NEXT_PUBLIC_APP_URL).hostname}
            src="https://c.wizecore.com/js/collecte.js"
          />
        )}

      <SWRConfig
        value={{
          fetcher,
          use: process.env.NEXT_PUBLIC_LOG_VERBOSE === "1" ? [swrLogger] : []
        }}
      >
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <Toaster />
          <ErrorBoundary>
            <ErrorReportProvider>
              <SessionProvider>
                <NuqsAdapter>
                  <TooltipProvider>
                    {process.env.NEXT_PUBLIC_ANALYTICS_CONSENT === "1" && (
                      <AnalyticsConsent
                        variant="small"
                        onConsent={consent => {
                          if (consent instanceof Date) {
                            sendEvent("consent", "update", {
                              ad_storage: "granted",
                              ad_user_data: "granted",
                              ad_personalization: "granted",
                              analytics_storage: "granted"
                            });
                          } else {
                            sendEvent("consent", "update", {
                              ad_storage: "denied",
                              ad_user_data: "denied",
                              ad_personalization: "denied",
                              analytics_storage: "denied"
                            });
                          }
                        }}
                      />
                    )}
                    <GoogleTagManager />
                    <Component {...pageProps} />
                  </TooltipProvider>
                </NuqsAdapter>
              </SessionProvider>
            </ErrorReportProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </SWRConfig>
    </main>
  );
}
